import { getAccessToken, invalidateToken } from '../auth/gis';

/** Thin wrapper over the Drive v3 REST API. No SDK: the handful of calls this
  * app makes are simpler as plain fetches, and it keeps the bundle small. */

const BASE = 'https://www.googleapis.com/drive/v3';
const UPLOAD_BASE = 'https://www.googleapis.com/upload/drive/v3';

export interface DriveFile {
   id: string;
   name: string;
   mimeType: string;
   size?: string;
   modifiedTime?: string;
   thumbnailLink?: string;
   parents?: string[];
}

export const FOLDER_MIME = 'application/vnd.google-apps.folder';

export class DriveError extends Error {
   constructor(
     message: string,
     readonly status: number,
   ) {
     super(message);
     this.name = 'DriveError';
   }
}

/** Performs an authenticated request, refreshing the token once on a 401. */
async function authed(url: string, init: RequestInit = {}, retry = true): Promise<Response> {
   const token = await getAccessToken({ interactive: false });
   const headers = new Headers(init.headers);
   headers.set('Authorization', `Bearer ${token}`);

   const response = await fetch(url, { ...init, headers, credentials: 'omit' });

   if ((response.status === 401 || response.status === 403) && retry) {
     invalidateToken();
     return authed(url, init, false);
  }
  return response;
}

async function json<T>(url: string, init?: RequestInit): Promise<T> {
   const response = await authed(url, init);
   if (!response.ok) {
     throw new DriveError(await describe(response), response.status);
   }
   return (await response.json()) as T;
}

async function describe(response: Response): Promise<string> {
   let detail = '';
   try {
     const body = (await response.json()) as { error?: { message?: string } };
     detail = body.error?.message ?? '';
   } catch {
     /* non-JSON error body */
   }

   switch (response.status) {
     case 401:
        return 'Google rejected the sign-in. Try connecting Drive again in Settings.';
     case 403:
        return detail.includes('rateLimit') || detail.includes('quota') ?
            'Google Drive is rate-limiting these requests. Try again shortly.'
          : `Google Drive refused the request${detail ? `: ${detail}` : ''}.`;
     case 404:
        return 'That Drive item no longer exists.';
     default:
        return `Drive request failed (${response.status})${detail ? `: ${detail}` : ''}`;
   }
}

const FILE_FIELDS = 'id,name,mimeType,size,modifiedTime,thumbnailLink';

/** Lists every child of a folder, following pagination to the end. */
export async function listChildren(folderId: string): Promise<DriveFile[]> {
   const files: DriveFile[] = [];
   let pageToken: string | undefined;

   do {
     const params = new URLSearchParams({
        q: `'${folderId.replace(/'/g, "\\'")}' in parents and trashed=false`,
        fields: `nextPageToken,files(${FILE_FIELDS})`,
        pageSize: '1000',
        orderBy: 'folder,name_natural',
        supportsAllDrives: 'true',
        includeItemsFromAllDrives: 'true',
     });
     if (pageToken) params.set('pageToken', pageToken);

     const page = await json<{ files?: DriveFile[]; nextPageToken?: string }>(
        `${BASE}/files?${params}`,
     );
     files.push(...(page.files ?? []));
     pageToken = page.nextPageToken;
   } while (pageToken);

   return files;
}

export function getFileMeta(fileId: string): Promise<DriveFile> {
   const params = new URLSearchParams({
     fields: FILE_FIELDS,
     supportsAllDrives: 'true',
   });
   return json<DriveFile>(`${BASE}/files/${encodeURIComponent(fileId)}?${params}`);
}

/** Byte range of a file, used by the m4b chapter parser to read atom headers
  * without downloading the whole thing. */
export async function fetchRange(
   fileId: string,
   start: number,
   end: number,
): Promise<ArrayBuffer> {
   const url = `${BASE}/files/${encodeURIComponent(fileId)}?alt=media&supportsAllDrives=true`;
   const response = await authed(url, { headers: { Range: `bytes=${start}-${end}` } });
   if (!response.ok && response.status !== 206) {
     throw new DriveError(await describe(response), response.status);
   }
   return response.arrayBuffer();
}

/** Downloads a whole file. Only used as the no-service-worker fallback — the
  * normal path streams byte ranges instead of pulling the lot into memory. */
export async function downloadFile(fileId: string): Promise<Blob> {
   const url = `${BASE}/files/${encodeURIComponent(fileId)}?alt=media&supportsAllDrives=true`;
   const response = await authed(url);
   if (!response.ok) throw new DriveError(await describe(response), response.status);
   return response.blob();
}

/* ------------------------------------------------------------------ *
  * appDataFolder — the hidden per-app folder holding the progress file
  * ------------------------------------------------------------------ */

const PROGRESS_FILENAME = 'audiobook-progress.json';

async function findProgressFileId(): Promise<string | null> {
   const params = new URLSearchParams({
     q: `name='${PROGRESS_FILENAME}' and trashed=false`,
     spaces: 'appDataFolder',
     fields: 'files(id,name)',
     pageSize: '10',
   });
   const result = await json<{ files?: DriveFile[] }>(`${BASE}/files?${params}`);
   return result.files?.[0]?.id ?? null;
}

export async function readAppData<T>(): Promise<T | null> {
   const fileId = await findProgressFileId();
   if (!fileId) return null;

   const response = await authed(`${BASE}/files/${fileId}?alt=media`);
   if (response.status === 404) return null;
   if (!response.ok) throw new DriveError(await describe(response), response.status);

   const text = await response.text();
   if (!text.trim()) return null;
   try {
     return JSON.parse(text) as T;
   } catch {
     // A corrupt progress file should not brick the library.
     return null;
   }
}

export async function writeAppData(payload: unknown): Promise<void> {
   const body = JSON.stringify(payload);
   const existingId = await findProgressFileId();

   if (existingId) {
     const response = await authed(
        `${UPLOAD_BASE}/files/${existingId}?uploadType=media`,
        { method: 'PATCH', body, headers: { 'Content-Type': 'application/json' } },
     );
     if (!response.ok) throw new DriveError(await describe(response), response.status);
     return;
   }

   // First write: create the file inside appDataFolder with a multipart upload.
   const boundary = `shelf-${Math.random().toString(36).slice(2)}`;
   const metadata = { name: PROGRESS_FILENAME, parents: ['appDataFolder'] };
   const multipart =
     `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n` +
     `${JSON.stringify(metadata)}\r\n` +
     `--${boundary}\r\nContent-Type: application/json\r\n\r\n` +
     `${body}\r\n--${boundary}--`;

   const response = await authed(`${UPLOAD_BASE}/files?uploadType=multipart`, {
     method: 'POST',
     body: multipart,
     headers: { 'Content-Type': `multipart/related; boundary=${boundary}` },
   });
   if (!response.ok) throw new DriveError(await describe(response), response.status);
}
